import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Annotation } from '@/services/annotationService';
import { logger } from '@/lib/logger';

export type ActionType =
  | 'CREATE_ANNOTATION'
  | 'UPDATE_ANNOTATION'
  | 'DELETE_ANNOTATION'
  | 'BATCH_DELETE_ANNOTATIONS'
  | 'PASTE_ANNOTATIONS'
  | 'UPDATE_PRODUCT'
  | 'UPDATE_FIXTURE'
  | 'UPDATE_CONTROL'
  | 'UPDATE_ROOM'
  | 'UPDATE_CABLE_JUNCTION'
  | 'UPDATE_CABLE_PATH'
  | 'MOVE_ENTITY'
  | 'ROTATE_ENTITY'
  | 'RESIZE_ENTITY'
  | 'BATCH_RESIZE_ENTITY';

interface Action {
  type: ActionType;
  timestamp: number;
  data: any;
  floorPlanId: string;
}

interface CreateAnnotationAction extends Action {
  type: 'CREATE_ANNOTATION';
  data: {
    annotation: Annotation;
  };
}

interface UpdateAnnotationAction extends Action {
  type: 'UPDATE_ANNOTATION';
  data: {
    id: string;
    oldData: Partial<Annotation>;
    newData: Partial<Annotation>;
  };
}

interface DeleteAnnotationAction extends Action {
  type: 'DELETE_ANNOTATION';
  data: {
    annotation: Annotation;
  };
}

interface BatchDeleteAnnotationsAction extends Action {
  type: 'BATCH_DELETE_ANNOTATIONS';
  data: {
    annotations: Annotation[];
  };
}

interface PasteAnnotationsAction extends Action {
  type: 'PASTE_ANNOTATIONS';
  data: {
    annotations: Annotation[];
  };
}

interface UpdateProductAction extends Action {
  type: 'UPDATE_PRODUCT';
  data: {
    id: string;
    oldData: any;
    newData: any;
  };
}

interface UpdateFixtureAction extends Action {
  type: 'UPDATE_FIXTURE';
  data: {
    id: string;
    oldData: any;
    newData: any;
  };
}

interface UpdateControlAction extends Action {
  type: 'UPDATE_CONTROL';
  data: {
    id: string;
    oldData: any;
    newData: any;
  };
}

interface UpdateRoomAction extends Action {
  type: 'UPDATE_ROOM';
  data: {
    id: string;
    oldData: any;
    newData: any;
  };
}

interface UpdateCableJunctionAction extends Action {
  type: 'UPDATE_CABLE_JUNCTION';
  data: {
    id: string;
    oldData: any;
    newData: any;
  };
}

interface UpdateCablePathAction extends Action {
  type: 'UPDATE_CABLE_PATH';
  data: {
    id: string;
    oldData: any;
    newData: any;
  };
}

interface MoveEntityAction extends Action {
  type: 'MOVE_ENTITY';
  data: {
    entityType: 'annotation' | 'product' | 'fixture' | 'control';
    id: string;
    oldPosition: { x: number; y: number };
    newPosition: { x: number; y: number };
  };
}

interface RotateEntityAction extends Action {
  type: 'ROTATE_ENTITY';
  data: {
    entityType: 'annotation' | 'product' | 'fixture' | 'control';
    id: string;
    oldRotation: number;
    newRotation: number;
  };
}

interface ResizeEntityAction extends Action {
  type: 'RESIZE_ENTITY';
  data: {
    entityType: 'annotation' | 'product' | 'fixture' | 'control';
    id: string;
    oldScale: number;
    newScale: number;
  };
}

interface BatchResizeEntityAction extends Action {
  type: 'BATCH_RESIZE_ENTITY';
  data: {
    entityType: 'annotation' | 'product' | 'fixture' | 'control';
    productIds: string[];
    oldScales: Record<string, number>;
    newScale: number;
    scope: 'single' | 'category' | 'subcategory';
    category: string;
    subcategory?: string | null;
  };
}

type UndoableAction =
  | CreateAnnotationAction
  | UpdateAnnotationAction
  | DeleteAnnotationAction
  | BatchDeleteAnnotationsAction
  | PasteAnnotationsAction
  | UpdateProductAction
  | UpdateFixtureAction
  | UpdateControlAction
  | UpdateRoomAction
  | UpdateCableJunctionAction
  | UpdateCablePathAction
  | MoveEntityAction
  | RotateEntityAction
  | ResizeEntityAction
  | BatchResizeEntityAction;

interface UndoRedoState {
  past: UndoableAction[];
  future: UndoableAction[];
  maxHistorySize: number;
}

interface UndoRedoActions {
  // Add actions to history
  addAction: (action: UndoableAction) => void;

  // Undo/Redo
  canUndo: () => boolean;
  canRedo: () => boolean;
  undo: () => UndoableAction | null;
  redo: () => UndoableAction | null;

  // Clear history
  clearHistory: () => void;
  clearHistoryForFloorPlan: (floorPlanId: string) => void;

  // Get action descriptions for UI
  getUndoDescription: () => string | null;
  getRedoDescription: () => string | null;
}

type UndoRedoStore = UndoRedoState & UndoRedoActions;

const initialState: UndoRedoState = {
  past: [],
  future: [],
  maxHistorySize: 50,
};

// Helper function to get action description
const getActionDescription = (action: UndoableAction): string => {
  switch (action.type) {
    case 'CREATE_ANNOTATION':
      return `Create ${action.data.annotation.type}`;
    case 'UPDATE_ANNOTATION':
      return 'Update annotation';
    case 'DELETE_ANNOTATION':
      return `Delete ${action.data.annotation.type}`;
    case 'BATCH_DELETE_ANNOTATIONS':
      return `Delete ${action.data.annotations.length} annotations`;
    case 'PASTE_ANNOTATIONS':
      return `Paste ${action.data.annotations.length} annotation${action.data.annotations.length > 1 ? 's' : ''}`;
    case 'UPDATE_PRODUCT':
      return 'Update product';
    case 'UPDATE_FIXTURE':
      return 'Update fixture';
    case 'UPDATE_CONTROL':
      return 'Update control';
    case 'UPDATE_ROOM':
      return 'Update room';
    case 'UPDATE_CABLE_JUNCTION':
      return 'Update cable junction';
    case 'UPDATE_CABLE_PATH':
      return 'Update cable path';
    case 'MOVE_ENTITY':
      return `Move ${action.data.entityType}`;
    case 'ROTATE_ENTITY':
      return `Rotate ${action.data.entityType}`;
    case 'RESIZE_ENTITY':
      return `Resize ${action.data.entityType}`;
    case 'BATCH_RESIZE_ENTITY':
      return `Batch resize ${action.data.productIds.length} ${action.data.entityType}${action.data.productIds.length > 1 ? 's' : ''}`;
    default:
      return 'Unknown action';
  }
};

export const useUndoRedoStore = create<UndoRedoStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    addAction: action => {
      set(state => {
        // Add the action to past
        const newPast = [...state.past, action];

        // Limit history size
        if (newPast.length > state.maxHistorySize) {
          newPast.shift();
        }

        // Clear future when a new action is performed
        return {
          past: newPast,
          future: [],
        };
      });

      logger.log('Added action to history:', getActionDescription(action));
    },

    canUndo: () => get().past.length > 0,

    canRedo: () => get().future.length > 0,

    undo: () => {
      const state = get();
      if (state.past.length === 0) return null;

      const action = state.past[state.past.length - 1];

      set({
        past: state.past.slice(0, -1),
        future: [action, ...state.future],
      });

      logger.log('Undo action:', getActionDescription(action));
      return action;
    },

    redo: () => {
      const state = get();
      if (state.future.length === 0) return null;

      const action = state.future[0];

      set({
        past: [...state.past, action],
        future: state.future.slice(1),
      });

      logger.log('Redo action:', getActionDescription(action));
      return action;
    },

    clearHistory: () => {
      set({
        past: [],
        future: [],
      });
      logger.log('Cleared all history');
    },

    clearHistoryForFloorPlan: floorPlanId => {
      set(state => ({
        past: state.past.filter(action => action.floorPlanId !== floorPlanId),
        future: state.future.filter(action => action.floorPlanId !== floorPlanId),
      }));
      logger.log('Cleared history for floor plan:', floorPlanId);
    },

    getUndoDescription: () => {
      const state = get();
      if (state.past.length === 0) return null;
      return getActionDescription(state.past[state.past.length - 1]);
    },

    getRedoDescription: () => {
      const state = get();
      if (state.future.length === 0) return null;
      return getActionDescription(state.future[0]);
    },
  })),
);

// Selector hooks
export const useCanUndo = () => useUndoRedoStore(state => state.canUndo());
export const useCanRedo = () => useUndoRedoStore(state => state.canRedo());
export const useUndoDescription = () => useUndoRedoStore(state => state.getUndoDescription());
export const useRedoDescription = () => useUndoRedoStore(state => state.getRedoDescription());
